import type { PresentedNode } from "../presentation/mesh";
import type { FocusState } from "../store/nexusStore";

export type SemanticZoomLevel = 0 | 1 | 2 | 3 | 4;
export type SemanticZoomState = { level: SemanticZoomLevel; blend: number; distance: number };

export function semanticZoomState(
  focus: FocusState,
  cameraDistance: number,
  target: SemanticZoomState = { level: 0, blend: 0, distance: cameraDistance },
): SemanticZoomState {
  target.distance = cameraDistance;
  if (focus.kind === "orbital") return assignZoom(target, 4, 1);
  if (focus.kind === "node") {
    if (cameraDistance < 10.5) return assignZoom(target, 4, 1);
    if (cameraDistance < 15) return assignZoom(target, 3, clamp01((15 - cameraDistance) / 4.5));
    return assignZoom(target, 2, clamp01((22 - cameraDistance) / 7));
  }
  if (cameraDistance < 36) return assignZoom(target, 2, clamp01((36 - cameraDistance) / 6));
  if (cameraDistance < 48) return assignZoom(target, 1, clamp01((48 - cameraDistance) / 12));
  return assignZoom(target, 0, 0);
}

function assignZoom(target: SemanticZoomState, level: SemanticZoomLevel, blend: number) {
  target.level = level;
  target.blend = blend;
  return target;
}


export function nodeVisibility(
  node: PresentedNode,
  state: SemanticZoomState,
  focusedNodeId: string | null,
  related: ReadonlySet<string>,
) {
  if (node.archetype === "black-hole") return 1;
  const level = state.level;
  if (level === 0) return node.kind === "system" ? 1 : 0;
  if (level === 1) {
    if (node.kind === "system") return 1;
    if (node.cartography.labelTier === "major") return 0.42 + state.blend * 0.38;
    return 0.08 + state.blend * 0.18;
  }
  if (!focusedNodeId) return node.kind === "system" ? 1 : 0.35 + state.blend * 0.25;
  if (node.id === focusedNodeId) return 1;
  if (related.has(node.id)) return 0.76 + state.blend * 0.2;
  if (level >= 4) return node.kind === "system" ? 0.12 : 0.025;
  return node.kind === "system" ? 0.2 : 0.055;
}

export function labelVisibility(
  kind: "node" | "orbital",
  hierarchy: "singularity" | "primary" | "secondary",
  state: SemanticZoomState,
  selected: boolean,
  related: boolean,
  orbitalVisible: boolean,
) {
  const level = state.level;
  if (kind === "orbital") return level >= 4 && orbitalVisible ? 1 : selected ? 1 : 0;
  if (hierarchy === "singularity") return 1;
  if (selected) return 1;
  if (level === 0) return hierarchy === "primary" ? 0.9 : 0;
  if (level === 1) return hierarchy === "primary" ? 1 : related ? 0.38 + state.blend * 0.28 : 0.08 * state.blend;
  if (level === 2) return related ? 0.82 + state.blend * 0.14 : hierarchy === "primary" ? 0.24 : 0.035;
  if (level >= 3) return related ? 0.95 : hierarchy === "primary" ? 0.16 : 0.02;
  return 0;
}

function clamp01(value: number) {
  return Math.max(0, Math.min(value, 1));
}
